"use client";

import { useState } from "react";

export interface AreaOfFocus {
  category: string;
  items: string[];
}

export interface AreasOfFocusSectionLabels {
  heading?: string;
  selectPrompt?: string;
  everydayFallbackSubtitle?: string;
  everydayIntro?: string;
  everydayFooter?: string;
  everydayItems?: {
    stress?: string;
    lowMood?: string;
    workAcademic?: string;
    lifeTransitions?: string;
    selfEsteem?: string;
    relationships?: string;
    burnout?: string;
    generalWellness?: string;
  };
}

export interface AreasOfFocusSectionProps {
  areasOfFocus: AreaOfFocus[];
  subtitle?: string;
  /** Optional label overrides for user-visible strings. Defaults keep
   *  the English copy so alli.io and other consumers are unaffected. */
  labels?: AreasOfFocusSectionLabels;
}

export function AreasOfFocusSection({ areasOfFocus, subtitle, labels }: AreasOfFocusSectionProps) {
  const [selectedCategory, setSelectedCategory] = useState<number | null>(0);

  const toggleCategory = (index: number) => {
    setSelectedCategory(selectedCategory === index ? null : index);
  };

  // Check if there are any areas of focus with items
  const hasAreasOfFocus = areasOfFocus && areasOfFocus.length > 0 &&
    areasOfFocus.some(area => area.items && area.items.length > 0);

  const headingLabel = labels?.heading ?? 'Areas of Focus';
  const selectPromptLabel =
    labels?.selectPrompt ?? 'Select a category to view specializations';
  const everydaySubtitleLabel =
    labels?.everydayFallbackSubtitle ?? 'Support for everyday mental wellness';
  const everydayIntroLabel =
    labels?.everydayIntro ??
    'This therapist provides supportive counseling for common day-to-day mental health concerns including:';
  const everydayFooterLabel =
    labels?.everydayFooter ??
    'Perfect for those seeking regular support and skill-building for everyday challenges.';
  const everyday = {
    stress: labels?.everydayItems?.stress ?? 'Everyday stress and anxiety',
    lowMood: labels?.everydayItems?.lowMood ?? 'Low mood and motivation',
    workAcademic:
      labels?.everydayItems?.workAcademic ?? 'Work and academic stress',
    lifeTransitions:
      labels?.everydayItems?.lifeTransitions ?? 'Life transitions',
    selfEsteem:
      labels?.everydayItems?.selfEsteem ?? 'Self-esteem and confidence',
    relationships:
      labels?.everydayItems?.relationships ?? 'Relationship concerns',
    burnout: labels?.everydayItems?.burnout ?? 'Burnout prevention',
    generalWellness:
      labels?.everydayItems?.generalWellness ?? 'General wellness support',
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm p-6">
      <div className="mb-4">
        <h2 className="text-2xl font-bold text-gray-900">{headingLabel}</h2>
        <p className="text-sm text-gray-600 mt-1">
          {hasAreasOfFocus ? (subtitle ?? selectPromptLabel) : everydaySubtitleLabel}
        </p>
      </div>

      {!hasAreasOfFocus ? (
        <div className="bg-gray-50 rounded-lg p-6">
          <p className="text-gray-700 leading-relaxed">
            {everydayIntroLabel}
          </p>
          <div className="mt-4 grid md:grid-cols-2 gap-3">
            <div className="space-y-2">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-primary-600 rounded-full mt-1.5 flex-shrink-0"></div>
                <span className="text-base text-gray-700">{everyday.stress}</span>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-primary-600 rounded-full mt-1.5 flex-shrink-0"></div>
                <span className="text-base text-gray-700">{everyday.lowMood}</span>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-primary-600 rounded-full mt-1.5 flex-shrink-0"></div>
                <span className="text-base text-gray-700">{everyday.workAcademic}</span>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-primary-600 rounded-full mt-1.5 flex-shrink-0"></div>
                <span className="text-base text-gray-700">{everyday.lifeTransitions}</span>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-primary-600 rounded-full mt-1.5 flex-shrink-0"></div>
                <span className="text-base text-gray-700">{everyday.selfEsteem}</span>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-primary-600 rounded-full mt-1.5 flex-shrink-0"></div>
                <span className="text-base text-gray-700">{everyday.relationships}</span>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-primary-600 rounded-full mt-1.5 flex-shrink-0"></div>
                <span className="text-base text-gray-700">{everyday.burnout}</span>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-primary-600 rounded-full mt-1.5 flex-shrink-0"></div>
                <span className="text-base text-gray-700">{everyday.generalWellness}</span>
              </div>
            </div>
          </div>
          <p className="text-gray-600 text-sm mt-4 italic">
            {everydayFooterLabel}
          </p>
        </div>
      ) : (
        <>
          {/* Mobile Layout - Accordion Style */}
          <div className="md:hidden space-y-2">
            {areasOfFocus.map((area, index) => (
          <div key={index}>
            <button
              onClick={() => toggleCategory(index)}
              className={`w-full flex items-center justify-between p-3 rounded-lg text-left transition-all ${
                selectedCategory === index
                  ? 'bg-primary-600 border-2 border-primary-700'
                  : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100'
              }`}
            >
              <span className={`font-medium ${
                selectedCategory === index ? 'text-white' : 'text-gray-900'
              }`}>
                {area.category}
              </span>
              <svg 
                className={`w-4 h-4 transition-transform ${
                  selectedCategory === index ? 'text-white rotate-90' : 'text-gray-400'
                }`} 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
            {selectedCategory === index && (
              <div className="mt-2 bg-gray-50 rounded-lg p-4">
                <div className="space-y-3">
                  {area.items.map((item, itemIndex) => (
                    <div key={itemIndex} className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-primary-600 rounded-full mt-1.5 flex-shrink-0"></div>
                      <span className="text-base text-gray-700 leading-relaxed">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Desktop Layout - Two Column */}
      <div className="hidden md:grid md:grid-cols-2 gap-6">
        {/* Left Column - Categories */}
        <div className="space-y-1">
          {areasOfFocus.map((area, index) => (
            <button
              key={index}
              onClick={() => setSelectedCategory(index)}
              className={`w-full flex items-center justify-between p-3 rounded-lg text-left transition-all ${
                selectedCategory === index
                  ? 'bg-primary-600 border-2 border-primary-700'
                  : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100'
              }`}
            >
              <span className={`font-medium ${
                selectedCategory === index ? 'text-white' : 'text-gray-900'
              }`}>
                {area.category}
              </span>
              <svg 
                className={`w-4 h-4 ${
                  selectedCategory === index ? 'text-white' : 'text-gray-400'
                }`} 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          ))}
        </div>
        
        {/* Right Column - Selected Category Items */}
        {selectedCategory !== null && (
          <div className="bg-gray-50 rounded-lg p-5">
            <h4 className="font-semibold text-lg text-gray-900 mb-4">
              {areasOfFocus[selectedCategory]?.category}
            </h4>
            <div className="space-y-3">
              {areasOfFocus[selectedCategory]?.items.map((item, index) => (
                <div key={index} className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-primary-600 rounded-full mt-1.5 flex-shrink-0"></div>
                  <span className="text-base text-gray-700 leading-relaxed">{item}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      </>
      )}
    </div>
  );
}